'use client';

/**
 * Custom Checkbox Component
 *
 * This is a controlled component that requires parent component management through
 * the "state" prop. The component supports three distinct states:
 *
 * - 'checked'     : Visually filled with a checkmark icon
 * - 'unchecked'   : Empty box with border
 * - 'indeterminate': Visually filled with a minus/dash icon (partial selection)
 */

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const checkboxVariants = cva(
  'peer aspect-square h-[14px] w-[14px] shrink-0 rounded-sm border-2 border-solid ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  {
    variants: {
      state: {
        checked: null,
        indeterminate: null,
        unchecked: 'border-venn-grey-60',
      },
      disabled: {
        true: 'cursor-not-allowed bg-venn-grey-40 bg-opacity-50 border-venn-grey-40',
        false: null,
      },
    },
    compoundVariants: [
      {
        state: 'checked',
        disabled: false,
        className:
          'border-venn-dark-blue bg-venn-dark-blue hover:bg-venn-deep-light-blue hover:border-venn-deep-light-blue',
      },
      {
        state: 'indeterminate',
        disabled: false,
        className:
          'border-venn-dark-blue bg-venn-dark-blue hover:bg-venn-deep-light-blue hover:border-venn-deep-light-blue',
      },
      {
        state: 'unchecked',
        disabled: false,
        className: 'hover:border-venn-deep-light-blue',
      },
    ],
    defaultVariants: {
      state: 'unchecked',
      disabled: false,
    },
  },
);

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & VariantProps<typeof checkboxVariants>
>(({ className, state = 'unchecked', disabled = false, ...props }, ref) => {
  const isChecked = state === 'checked';
  const isIndeterminate = state === 'indeterminate';

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(checkboxVariants({ state, disabled }), className)}
      checked={isChecked || isIndeterminate}
      {...props}
    >
      <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center')}>
        {state === 'indeterminate' ? (
          <Minus className="h-3 w-3 text-venn-white" strokeWidth={5} />
        ) : (
          <Check className="h-3 w-3 text-venn-white" strokeWidth={5} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
